import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { toast } from 'react-toastify';
import { TextField } from '@mui/material';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { AdminProfileDetails } from 'typings/entities/profile';
import convertingUndefinedToString from 'utils/convertingUndefinedToString';
import { putAdminProfileDetails } from 'http/profile';
import { getError } from 'utils/error';
import { setUser, getUser } from 'services/localStorage';
import Button from 'components/Button';
import * as Sentry from '@sentry/browser';
import { theme } from 'theme';

import validationSchema from './validationSchema';
import * as S from '../../styled';

const AdminDetailsForm = () => {
  const profileData = getUser();
  const history = useHistory();
  const { t } = useTranslation('common');
  const schema = useMemo(() => validationSchema(t), [t]);

  const [, updateAdminProfileDetails] = useAsyncFn(async (req: AdminProfileDetails) => {
    try {
      const newReq: any = convertingUndefinedToString(req);

      const data = {
        email: newReq.email,
      };

      const result = await putAdminProfileDetails(data);
      setUser(result.data);
    } catch (err: any) {
      Sentry.captureException(new Error(getError(err).error));
      toast.error(getError(err).error);
    }
  }, []);

  const defaultValues = {
    email: profileData.email,
  };

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = useForm<AdminProfileDetails>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const handleEditProfileInfo = (req: AdminProfileDetails) => {
    updateAdminProfileDetails(req);
  };

  const handleFormCancel = () => {
    history.goBack();
  };

  return (
    <form onSubmit={handleSubmit(handleEditProfileInfo)}>
      <S.Container>
        <S.Wrapper>
          <S.Text>
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  InputLabelProps={{
                    shrink: true,
                    required: true,
                  }}
                  size="small"
                  type="email"
                  onChange={onChange}
                  value={value}
                  error={!!errors.email}
                  label={t('profilePage.emailLabel')}
                  disabled={isSubmitting}
                />
              )}
            />
            <S.Error>
              <ErrorMessage name="email" errors={errors} />
            </S.Error>
          </S.Text>
        </S.Wrapper>
      </S.Container>
      <S.Container>
        <S.ContentWrapper largeSize>
          <S.ButtonWrapper>
            {' '}
            <Button
              textButton={t('saveButton')}
              buttonProps={{
                bgColor: theme.colors.primary,
                hoverColor: theme.colors.primaryShadow,
                variant: 'contained',
                disabled: isSubmitting,
                type: 'submit',
                startIcon: <DoneOutlinedIcon />,
              }}
            />
          </S.ButtonWrapper>
          <S.ButtonWrapper>
            <Button
              textButton={t('cancelButton')}
              buttonProps={{
                bgColor: theme.colors.grey,
                hoverColor: theme.colors.greyShadow,
                variant: 'outlined',
                disabled: isSubmitting,
                onClick: handleFormCancel,
                startIcon: <ClearOutlinedIcon />,
              }}
            />
          </S.ButtonWrapper>
        </S.ContentWrapper>
      </S.Container>
    </form>
  );
};

export default AdminDetailsForm;
